import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, LayoutGrid, Map as MapIcon } from "lucide-react";
import api from "@/lib/api";
import PropertyCard, { formatPrice } from "@/components/PropertyCard";
import MapView from "@/components/MapView";

const CITIES = ["Lucknow", "Gurugram", "Noida", "Delhi", "Mumbai", "Bangalore", "Pune", "Hyderabad", "Dubai", "Singapore"];
const TYPES = ["apartment", "villa", "plot", "commercial"];
const STATUS = ["ready", "under_construction", "new_launch"];
const BHKS = [1, 2, 3, 4, 5];

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState("grid");

  const city = params.get("city") || "";
  const type = params.get("type") || "";
  const status = params.get("status") || "";
  const bhk = params.get("bhk") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (city) q.set("city", city);
    if (type) q.set("type", type);
    if (status) q.set("status", status);
    if (bhk) q.set("bhk", bhk);
    if (search) q.set("search", search);
    api.get(`/projects?${q.toString()}`)
      .then((r) => setProjects(r.data || []))
      .finally(() => setLoading(false));
  }, [city, type, status, bhk, search]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const clear = () => setParams(new URLSearchParams());

  const FilterGroup = ({ title, options, current, onSelect, testIdPrefix }) => (
    <div className="border-b border-slate-100 py-5">
      <div className="overline mb-3">{title}</div>
      <div className="space-y-1.5">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onSelect(current === o.value ? "" : o.value)}
            data-testid={`${testIdPrefix}-${o.value}`}
            className={`block w-full text-left text-sm py-1.5 px-3 rounded-lg transition-colors ${
              current === o.value ? "bg-amber-50 text-[#D4A437] font-semibold" : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            {current === o.value ? "● " : "○ "}{o.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-10" data-testid="listings-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-10">
        <div className="overline mb-3">Inventory · {projects.length} {projects.length === 1 ? "result" : "results"}</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-slate-900">All Projects</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">Browse RERA-verified projects from leading developers across ten cities — eight in India and two overseas.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8 pb-24">
        {/* Filters */}
        <aside className={`lg:col-span-3 ${filtersOpen ? "block fixed inset-0 z-40 bg-white p-6 overflow-auto" : "hidden lg:block"}`}>
          <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-serif text-xl font-bold text-slate-900">Filters</h3>
              <button onClick={clear} className="text-xs font-semibold text-[#D4A437] hover:text-[#B88C2E]" data-testid="clear-filters-btn">Clear all</button>
              <button className="lg:hidden text-slate-700" onClick={() => setFiltersOpen(false)}><X className="w-5 h-5" /></button>
            </div>

            <FilterGroup title="City" current={city} onSelect={(v) => update("city", v)} testIdPrefix="filter-city" options={CITIES.map(c => ({ value: c, label: c }))} />
            <FilterGroup title="Type" current={type} onSelect={(v) => update("type", v)} testIdPrefix="filter-type" options={TYPES.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} />
            <FilterGroup title="Status" current={status} onSelect={(v) => update("status", v)} testIdPrefix="filter-status" options={STATUS.map(s => ({ value: s, label: s.replace("_", " ").replace(/\b\w/g, m => m.toUpperCase()) }))} />
            <FilterGroup title="BHK" current={bhk} onSelect={(v) => update("bhk", v)} testIdPrefix="filter-bhk" options={BHKS.map(b => ({ value: String(b), label: `${b} BHK` }))} />
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setFiltersOpen(true)} data-testid="mobile-filter-btn" className="lg:hidden px-4 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-2 bg-white shadow-sm">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
            <div className="ml-auto flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
              <button
                onClick={() => setView("grid")}
                data-testid="view-grid-btn"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-2 transition-colors ${view === "grid" ? "bg-[#D4A437] text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <LayoutGrid className="w-4 h-4" /> Grid
              </button>
              <button
                onClick={() => setView("map")}
                data-testid="view-map-btn"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-2 transition-colors ${view === "map" ? "bg-[#D4A437] text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <MapIcon className="w-4 h-4" /> Map
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="aspect-[4/3] bg-slate-100 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="py-32 text-center bg-white rounded-2xl border border-slate-100">
              <div className="overline mb-4">No matches</div>
              <h3 className="font-serif text-2xl font-bold text-slate-900">Try a different filter combination.</h3>
            </div>
          ) : view === "map" ? (
            <MapView
              height={620}
              markers={projects.map((p) => ({
                lat: p.lat, lng: p.lng, name: p.name, locality: p.locality, city: p.city,
                price: formatPrice(p.price_min, p.currency),
              }))}
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {projects.map((p) => <PropertyCard key={p.id} project={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
